/*
 * The Pains round, all four phases. From the prototype's pain screens.
 *
 * The round is one exercise read four ways: everyone writes alone, the notes go
 * face up, the room spends its votes blind, and then the count is shown. The
 * copy carries that — `writePrivate` is a promise the screen has to keep, and
 * `voteBlind` is why the tally is a separate phase rather than a toggle.
 */

export const PAINS_COPY = {
  /** Facilitator, while the room writes. */
  watchTitle: "Time to write the pain points.",
  watchBody:
    "Write the things that slow the business down day to day. Be specific: a real moment, who it hit, and what it cost.",
  stateWriting: "Writing",
  stateDone: "Done",

  /** The participant's own screen. */
  writePrivate: "Only you can see this",
  writeTitle: "Where does it hurt?",
  /*
   * The brief carries the number, so the room is told how many notes it owes
   * rather than being left to count empty slots.
   */
  writeBrief: (limit: number) =>
    `Write ${limit} pain point${limit === 1 ? "" : "s"}: name the things that slow the business down day to day.`,
  writePlaceholder: "We find out we're short on drivers on Monday morning…",
  editPlaceholder: "Write the incident",
  addNote: "Place it down",
  addHint: "Enter adds it. Shift + Enter for a new line.",
  removeNote: "Delete",
  brief: (written: number, limit: number) => `${written} of ${limit} written`,
  capHit: (limit: number) =>
    `All ${limit} written. Edit any of them until the facilitator reveals.`,
  imDone: "I'm done",
  imDoneWaiting: "Done, waiting on the facilitator",
  /** The button's own label once locked in — the full sentence moved to
   *  `votesLockedTooltip`, read on hover/focus instead of taking over the
   *  button's own text. */
  lockedIn: "Locked In",

  /** Reveal. */
  revealTitle: "Time to reveal everyone's answers.",
  revealHeadline: (count: number) => `${count} ${count === 1 ? "pain" : "pains"}, face up.`,
  revealBody:
    "Read them out without commenting. Notice which two are the same pain wearing different clothes.",

  /** Vote. */
  voteTitle: "Voting time!",
  voteBlind: "Voting time!",
  /** The room's own line, shown identically to the facilitator and every
   *  participant — one fact everyone in the room is reading the same way. */
  voteWatchBody: "Each participant casts their votes.",
  /*
   * The roster's line while the votes are being cast — "done" here means the
   * votes are locked in, which is the thing the facilitator is waiting on before
   * revealing the tally.
   */
  votedCount: (done: number, total: number) => `${done} of ${total} voted`,
  /** Counts down, not up: what you still have to spend is the useful number. */
  budget: (left: number) =>
    left === 0 ? "No votes left" : `You have ${left} vote${left === 1 ? "" : "s"} left`,
  votesLockedTooltip: "Votes are in. The tally stays hidden until reveal.",
  noteVoteCount: (n: number) => `${n} vote${n === 1 ? "" : "s"}`,

  /** Tally. */
  tallyTitle: "The room has spoken.",
  /*
   * The count recommends; it does not decide. Nothing converts the top pain into
   * a job on its own — the facilitator picks one on the next screen — so the line
   * cannot promise the room that the winner is already locked in.
   */
  tallyBody: "The top pain point is the room's answer. Choose the one this session runs on.",
  /*
   * A tie has no top pain, so the board shows none — and the line has to stop
   * claiming there is one, or the room reads the sentence and starts looking
   * for a badge that is deliberately absent.
   */
  tallyTie: (n: number) =>
    `${n} pains are tied at the top. Talk it through, then take one forward.`,
  /** Revealed before anyone voted — a tie of nothing is not a tie. */
  tallyNoVotes: "No votes have been cast yet.",
  /** The top three vote bands, badged on the note itself rather than in its
   *  footer pill — see `cornerTag` on `PaperNote`. */
  theWinner: "The winner",
  tiedFirst: "Tied 1st",
  runnerUp: "Runner-up",
  third: "Third",
  voteCount: (n: number) => `${n} ${n === 1 ? "vote" : "votes"}`,
  yourVote: "Yours",

  /** Empty and attribution. */
  emptyBoard: "Nothing has been written yet.",
  loadedBy: "Loaded before the session",
} as const
